import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { ErrorModel } from '../../../core/cms-api/models';
import { MessageTranslateService } from '../../../shared/services/message.service';
import * as operatorsActions from '../../../store/operators/operators.actions';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import { SmartCarsService } from '../services/smart-cars.service';
import * as smartCarActions from './smart-car.actions';
import * as smartCarSelectors from './smart-car.selectors';

const toErrorModel = (err: any): ErrorModel => ({
  errorMessage: err?.message ?? 'ERROR',
});

export class SmartCarsEffects {
  actions$: Actions = inject(Actions);
  store: Store = inject(Store);
  smartCarsService = inject(SmartCarsService);
  messageService = inject(MessageTranslateService);

  changeSmartCarPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartCarActions.changePagination),
      map((_) => smartCarActions.changePaginationSuccessful())
    )
  );

  loadSmartCarList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        smartCarActions.loadSmartCarList,
        smartCarActions.changePaginationSuccessful,
        smartCarActions.createSmartCarSuccess,
        smartCarActions.updateSmartCarSuccess,
        smartCarActions.deleteSmartCarSuccess,
        smartCarActions.disconnectSmartCarSuccess,
        operatorsActions.SetCurrentOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(smartCarSelectors.selectCurrentPagination),
        this.store.select(operatorsSelectors.selectCurrentOperator),
      ]),
      filter(
        ([_action, _selectedPagination, currentOperator]) =>
          currentOperator?.id != null
      ),
      switchMap(([_action, selectedPagination, currentOperator]) =>
        this.smartCarsService
          .getSmartCars(
            currentOperator!.id!,
            selectedPagination?.pageIndex ?? 0,
            selectedPagination?.pageSize ?? 10
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return smartCarActions.loadSmartCarListSuccess({
                  smartCarsResponse: res.value!,
                });
              }
              return smartCarActions.loadSmartCarListFailure({
                error: res.error!,
              });
            }),
            catchError((err) =>
              of(smartCarActions.loadSmartCarListFailure({ error: toErrorModel(err) }))
            )
          )
      )
    )
  );

  createSmartCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartCarActions.createSmartCar),
      switchMap((request) =>
        this.smartCarsService.createSmartCar(request.smartCarRequest).pipe(
          map((res) => {
            if (res.isSuccess) {
              return smartCarActions.createSmartCarSuccess({
                smartCarId: res.value!,
              });
            }
            return smartCarActions.createSmartCarFailure({ error: res.error! });
          }),
          catchError((err) =>
            of(smartCarActions.createSmartCarFailure({ error: toErrorModel(err) }))
          )
        )
      )
    )
  );

  updateSmartCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartCarActions.updateSmartCar),
      switchMap((request) =>
        this.smartCarsService
          .updateSmartCar(request.smartCarId, request.smartCarRequest)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return smartCarActions.updateSmartCarSuccess({
                  smartCarId: request.smartCarId,
                });
              }
              return smartCarActions.updateSmartCarFailure({ error: res.error! });
            }),
            catchError((err) =>
              of(smartCarActions.updateSmartCarFailure({ error: toErrorModel(err) }))
            )
          )
      )
    )
  );

  deleteSmartCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartCarActions.deleteSmartCar),
      switchMap((request) =>
        this.smartCarsService.deleteSmartCar(request.smartCarId).pipe(
          map((res) => {
            if (res.isSuccess) {
              return smartCarActions.deleteSmartCarSuccess();
            }
            return smartCarActions.deleteSmartCarFailure({ error: res.error! });
          }),
          catchError((err) =>
            of(smartCarActions.deleteSmartCarFailure({ error: toErrorModel(err) }))
          )
        )
      )
    )
  );

  /** Revokes the vehicle's Smartcar access; the list reloads to show the new connection state. */
  disconnectSmartCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartCarActions.disconnectSmartCar),
      switchMap((request) =>
        this.smartCarsService.disconnectSmartCar(request.smartCarId).pipe(
          map((res) => {
            if (res.isSuccess) {
              return smartCarActions.disconnectSmartCarSuccess();
            }
            return smartCarActions.disconnectSmartCarFailure({ error: res.error! });
          }),
          catchError((err) =>
            of(smartCarActions.disconnectSmartCarFailure({ error: toErrorModel(err) }))
          )
        )
      )
    )
  );

  showSuccessNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          smartCarActions.createSmartCarSuccess,
          smartCarActions.updateSmartCarSuccess,
          smartCarActions.deleteSmartCarSuccess,
          smartCarActions.disconnectSmartCarSuccess
        ),
        map((action) => {
          if (action.type == smartCarActions.createSmartCarSuccess.type)
            this.messageService.successTranslate('smartCars.notifications.create-success');
          if (action.type == smartCarActions.updateSmartCarSuccess.type)
            this.messageService.successTranslate('smartCars.notifications.update-success');
          if (action.type == smartCarActions.deleteSmartCarSuccess.type)
            this.messageService.successTranslate('smartCars.notifications.delete-success');
          if (action.type == smartCarActions.disconnectSmartCarSuccess.type)
            this.messageService.successTranslate('smartCars.notifications.disconnect-success');
        })
      ),
    {
      dispatch: false,
    }
  );

  showErrorNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          smartCarActions.loadSmartCarListFailure,
          smartCarActions.createSmartCarFailure,
          smartCarActions.updateSmartCarFailure,
          smartCarActions.deleteSmartCarFailure,
          smartCarActions.disconnectSmartCarFailure
        ),
        map((action) => {
          if (action.type == smartCarActions.loadSmartCarListFailure.type)
            this.messageService.errorTranslate('smartCars.notifications.load-failure');
          if (action.type == smartCarActions.createSmartCarFailure.type)
            this.messageService.errorTranslate('smartCars.notifications.create-failure');
          if (action.type == smartCarActions.updateSmartCarFailure.type)
            this.messageService.errorTranslate('smartCars.notifications.update-failure');
          if (action.type == smartCarActions.deleteSmartCarFailure.type)
            this.messageService.errorTranslate('smartCars.notifications.delete-failure');
          if (action.type == smartCarActions.disconnectSmartCarFailure.type)
            this.messageService.errorTranslate('smartCars.notifications.disconnect-failure');
        })
      ),
    {
      dispatch: false,
    }
  );
}
